/**
 * M3C-04 — `useWorkflows()`: the workflows a user may start work with
 * (DI-D62 step 0; DI-D9 c1 as extended by DI-D64 c3: own workflows + the
 * injection nodes shared with the tenant).
 *
 *   GET /api/workflows/v1/workflows   (`listWorkflows`, workflows/v1 1.2.0)
 *
 * Own rows (`shared: false`) carry their whole latest active version; a shared
 * row (`shared: true`, M3C-02) carries ONLY its injection node and names its
 * owner (DI-D59: a tenant gives access to a node, never its chain).
 * `isDefault` marks the tenant's default (M3C-07's seed); the FE keeps no
 * workflow key literal (lock `no-fe-registry-of-keys`).
 *
 * Replaces the intake's `usePresetCatalogue` as the CHOICE list (the
 * project wizard, the project Workflow panel / bulk dialog, the intent menu,
 * the row select). The inspection-3 list stays on the wire; the intake keeps
 * reading it only as the preset catalogue that names a legacy key's facts
 * (`intake/api/workflows.ts:usePresetCatalogue`).
 *
 * Tenant-scoped, so the query is `enabled`-gated on tenant readiness.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import type { WorkflowsComponents } from "@aeriosiq/types";
import { workflowsFetch } from "./workflowsFetch.js";
import { nodeForModule } from "../moduleRegistry.js";

export type Workflow = WorkflowsComponents["schemas"]["Workflow"];
export type WorkflowVersion = WorkflowsComponents["schemas"]["WorkflowVersion"];
export type WorkflowNode = WorkflowsComponents["schemas"]["WorkflowNode"];
export type WorkflowList = WorkflowsComponents["schemas"]["WorkflowList"];

export const workflowKeys = {
  all: ["workflows"] as const,
  list: () => [...workflowKeys.all, "list"] as const,
};

export function fetchWorkflows(): Promise<Workflow[]> {
  return workflowsFetch<WorkflowList>("/workflows").then((r) => r.items);
}

export function useWorkflows() {
  const tenantReady = useAuthStore((s) => Boolean(s.tenant?.tenantSlug));
  return useQuery<Workflow[]>({
    queryKey: workflowKeys.list(),
    queryFn: fetchWorkflows,
    enabled: tenantReady,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// ── reading a workflow ──────────────────────────────────────────────────

/** The tenant's own workflows, in the server's order (the default first). */
export function ownWorkflows(
  list: readonly Workflow[] | undefined,
): Workflow[] {
  return (list ?? []).filter((w) => !w.shared);
}

/** The injection nodes shared with the tenant (owner name, then name). */
export function sharedWorkflows(
  list: readonly Workflow[] | undefined,
): Workflow[] {
  return (list ?? []).filter((w) => w.shared);
}

/** The tenant's default (`isDefault`, at most one); null before the seed. */
export function defaultWorkflowOf(
  list: readonly Workflow[] | undefined,
): Workflow | null {
  return ownWorkflows(list).find((w) => w.isDefault) ?? null;
}

export function workflowByVersionId(
  list: readonly Workflow[] | undefined,
  versionId: string | null | undefined,
): Workflow | null {
  if (!versionId) return null;
  return (
    (list ?? []).find((w) => w.latestVersion.versionId === versionId) ?? null
  );
}

/** Own workflows whose start node mounts the project wizard (step 0 of a project). */
export function projectWorkflows(
  list: readonly Workflow[] | undefined,
): Workflow[] {
  return ownWorkflows(list).filter(
    (w) => nodeForModule(w.latestVersion, "projects.wizard") !== null,
  );
}

/** Own + shared workflows with a node the drop mounts (step 0 of a drop). */
export function injectionWorkflows(
  list: readonly Workflow[] | undefined,
): Workflow[] {
  return (list ?? []).filter(
    (w) => nodeForModule(w.latestVersion, "intake.dropAndDecide") !== null,
  );
}

/**
 * "Zeitview workflow — shared by Zeitview" (story fe-files 4): a shared node
 * is named by its OWNER (XT-D9: the owner's name crosses to the grantee),
 * never by the owner's internal workflow name.
 */
export function sharedWorkflowLabel(
  w: Pick<Workflow, "ownerTenantName">,
): string {
  const owner = w.ownerTenantName ?? "Partner";
  return `${owner} workflow — shared by ${owner}`;
}

/** The short form of the menu ("Zeitview workflow"). */
export function sharedWorkflowShort(
  w: Pick<Workflow, "ownerTenantName">,
): string {
  return `${w.ownerTenantName ?? "Partner"} workflow`;
}

/** A workflow's display name: own → its name; shared → the owner's words. */
export function workflowDisplayName(
  w: Pick<Workflow, "shared" | "name" | "ownerTenantName">,
): string {
  return w.shared ? sharedWorkflowShort(w) : w.name;
}

/**
 * The OWNER's tenant slug a set created under this workflow must send as
 * `X-Tenant-ID` (M3C-02: the shared drop names the owner; the server derives
 * the schema from the version, never from the header). workflows/v1 1.3.0
 * carries it on every row (`ownerTenantSlug`, the access resolver's validated
 * share row — exactly what the gate compares the header with). Own workflow
 * → null (= the caller's own slug, sent by the fetch helpers by default).
 */
export function ownerSlugOf(
  w: Pick<Workflow, "shared" | "ownerTenantSlug">,
): string | null {
  return w.shared ? w.ownerTenantSlug : null;
}

// ── the injection node's facts (SERVED, workflows/v1 1.10.0) ──────────────
//
// M5-VER-01 — companies 0048 retired the injection node's `preset` /
// `imagesUploaded` / `export` params (they now say nothing); the gateway
// DERIVES the facts from the FULL version (the Upload step's `imageUpload` +
// the client end nodes) and serves them on the injection node, own AND
// shared items alike (a shared item's facts come from the owner's version
// before the cut). The FE reads those three fields and NOTHING off `params`.

export type ExportKind = "none" | "write_back" | "skyspecs_json";

/**
 * M5-NODE-01 (workflows/v1 1.7.0) — the version's image upload mode, SERVED
 * on the injection node (`WorkflowNode.imageUpload`, derived by the gateway's
 * `injectionFactsOf`): `automatic` — uploaded, "now" or "after verification";
 * `optional` — the pilot decides per data set (don't upload preselected) and
 * may upload later with a reason (the escape hatch); `blocked` — no upload
 * anywhere (the server refuses a session, 409 `upload_blocked`).
 */
export type ImageUploadMode = "automatic" | "optional" | "blocked";

export interface InjectionFacts {
  /**
   * workflows/v1 1.10.0 — the served `legacyKey`: the legacy `workflow.*`
   * key the version MAPS TO (the gateway's `legacyKeyOf`), the alias the
   * `workflow` string column speaks until M5-DROP-01; null when the version
   * maps to none. Not a fact — the key a set's plan / a DI-3-C intent names.
   */
  legacyKey: string | null;
  /** `imageUpload === "automatic"` (the gateway's rule). */
  imagesUploaded: boolean;
  /** The served `export` (1.10.0); absent → `none`. */
  export: ExportKind;
  /** The served `imageUpload` (1.7.0); absent → `automatic` (1.9.0). */
  imageUpload: ImageUploadMode;
}

/** The served mode, or null when the value is not one this release knows. */
export function imageUploadOf(v: unknown): ImageUploadMode | null {
  return v === "automatic" || v === "optional" || v === "blocked" ? v : null;
}

/**
 * The facts of a LEGACY KEY, as the server's preset catalogue
 * (inspection-3 `GET /workflows`) names them — read only for a key a
 * project or a set carries WITHOUT a version (`proposal.ts` `factsOfKey`);
 * never for a version, whose injection node serves its own facts.
 */
export type PresetFactsLookup = (
  preset: string,
) => { imagesUploaded: boolean; export: ExportKind } | null;

function exportOf(v: unknown): ExportKind | null {
  return v === "none" || v === "write_back" || v === "skyspecs_json" ? v : null;
}

/**
 * The injection node's facts, as SERVED (workflows/v1 1.10.0): `imageUpload`
 * (absent → `automatic`), `export` (absent → `none`), `legacyKey` (absent →
 * null). `params` are never read (M5-VER-01: the retired keys may still sit
 * on a legacy draft, tolerated and read by nothing). Null when the version
 * has no injection node.
 */
export function injectionFactsOf(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
): InjectionFacts | null {
  const node = nodeForModule(version, "intake.dropAndDecide");
  if (!node) return null;
  const imageUpload = imageUploadOf(node.imageUpload) ?? "automatic";
  return {
    legacyKey: typeof node.legacyKey === "string" ? node.legacyKey : null,
    imagesUploaded: imageUpload === "automatic",
    export: exportOf(node.export) ?? "none",
    imageUpload,
  };
}

/**
 * workflows/v1 1.12.0 (M6-NODE-SORT-01 §8.3 rule 8) — what the version's
 * enabled client nodes need of the browser, as SERVED on the injection node
 * (`versionClientRequirements`, derived by the gateway from the FULL version
 * — on a SHARED item from the owner's version before the cut). Null when the
 * field is absent (an older gateway) or the version has no injection node:
 * the caller then falls back to the `export` fact.
 */
export function versionClientRequirementsOf(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
): string[] | null {
  const node = nodeForModule(version, "intake.dropAndDecide");
  const served: unknown = node?.versionClientRequirements;
  if (!Array.isArray(served)) return null;
  return (served as readonly unknown[]).filter(
    (r): r is string => typeof r === "string",
  );
}

/**
 * DI-5-3 — the words naming the client end node a served `export` fact
 * stands for (a SHARED item, whose end node is not served): the same words
 * the kind words below use. Null for `none`.
 */
export function exportTitle(e: "write_back" | "skyspecs_json"): string;
export function exportTitle(e: ExportKind): string | null;
export function exportTitle(e: ExportKind): string | null {
  if (e === "write_back") return "Write-back";
  if (e === "skyspecs_json") return "SkySpecs export";
  return null;
}

/** "images uploaded" / "images stay on this computer · write-back" — kind words. */
export function injectionKindWords(f: InjectionFacts | null): string | null {
  if (!f) return null;
  if (f.imagesUploaded) return "images uploaded";
  if (f.export === "write_back")
    return "images stay on this computer · write-back";
  if (f.export === "skyspecs_json")
    return "images stay on this computer · SkySpecs export";
  return "images stay on this computer";
}
